import { createClient, SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { supabase as cloudSupabase } from './client';
import { EXTERNAL_SUPABASE_URL, EXTERNAL_SUPABASE_ANON_KEY } from '@/config/externalSupabase';


/**
 * External Supabase client — used for all database reads/writes.
 * Points to your external Supabase project where you have full dashboard access.
 */
export const externalSupabase = createClient<Database>(
  EXTERNAL_SUPABASE_URL,
  EXTERNAL_SUPABASE_ANON_KEY,
  {
    auth: {
      storage: localStorage,
      persistSession: true,
      autoRefreshToken: true,
    },
  }
);

/**
 * Hybrid client export:
 * - `.from()`, `.storage`, `.channel()`, `.removeChannel()`, `.rpc()` → external DB
 * - `.auth`, `.functions` → Lovable Cloud (for session management & edge functions)
 * 
 * This allows all existing code to just change their import path without
 * changing any variable names or method calls.
 */
export const supabase = new Proxy(externalSupabase, {
  get(target, prop, receiver) {
    // Route auth and functions to Lovable Cloud client
    // Auth must go to Cloud because session tokens are issued by Cloud auth
    if (prop === 'functions' || prop === 'auth') {
      return (cloudSupabase as any)[prop];
    }
    // Everything else (from, storage, channel, rpc, etc.) goes to external
    return Reflect.get(target, prop, receiver);
  },
}) as SupabaseClient<Database>;
